// Conversion-event taxonomy for content→revenue measurement.
//
// These power OpenPanel funnels (traffic → CTA click → booking/outbound). The
// OpenPanel client is created with `trackAttributes: true` (see analytics.ts),
// which attaches a global click listener that fires an event named by an
// element's `data-track` value, with every other `data-*` attribute as a
// (camelCased) property. So instrumenting a CTA is just spreading the result of
// `trackAttrs()` onto a <Link> or <a> — no per-click handler, no imports at the
// call site beyond this helper.
//
// `data-location` records WHERE the CTA was clicked (home_hero, nav_desktop, …)
// so a funnel can tell which placement actually converts.
//
// A few conversions are not a link click and so cannot be expressed as an
// attribute: a redirect to Stripe, a signup the API accepted. Those are fired
// from code through the helpers at the foot of this file, but they stay in this
// file so the whole taxonomy is readable in one place.
import { analytics } from './analytics'

export const CTA = {
  /** Start of the on-site booking flow — the primary revenue conversion. */
  bookCall: 'cta_book_call',
  /** A session request actually submitted (bottom of the booking funnel). */
  bookingRequest: 'cta_booking_request',
  /** Internal link to the /mentorship page (mid-funnel intent). */
  mentorship: 'cta_mentorship',
  /** Internal link to the /courses page. */
  courses: 'cta_courses',
  /** Maven paid-cohort outbound (Senior→Staff, AI/Agents). */
  maven: 'cta_maven',
  /** Free Maven Lightning Lesson outbound (top of funnel). */
  lesson: 'cta_lesson',
  /** Amazon affiliate outbound from a book review. */
  amazon: 'cta_amazon',
} as const

export type CtaEvent = (typeof CTA)[keyof typeof CTA]

/**
 * Conversions fired from code rather than from a `data-track` attribute,
 * because each marks something that HAPPENED (a redirect to Stripe, a waitlist
 * row the API accepted) rather than a click on a link.
 */
export const EVENT = {
  /** A Stripe Checkout session exists and the browser is about to go to it. */
  checkoutStart: 'cta_checkout_start',
  /** Enrol pressed while signed out, so the click went to /login, not Stripe. */
  checkoutBlocked: 'cta_checkout_blocked',
  /** The session could not be created; the buyer is still on the page. */
  checkoutFailed: 'cta_checkout_failed',
  /** A waitlist row the API accepted. Success only, never on submit. */
  newsletterSignup: 'cta_newsletter_signup',
} as const

type CheckoutEvent =
  | typeof EVENT.checkoutStart
  | typeof EVENT.checkoutBlocked
  | typeof EVENT.checkoutFailed

/**
 * Report where a press of "Unlock full course" ended up.
 *
 * The three outcomes are separate events on purpose. In the 30 days to 29 Aug
 * 2026 /courses drew 167 filtered sessions and produced one Stripe checkout
 * session, and nothing said whether the rest never pressed the button, pressed
 * it signed out and got bounced to /login, or reached Stripe and left. Those
 * need different fixes, so a single event with an outcome property would still
 * have to be split to be acted on.
 *
 * `reason` carries a coarse cause or our own API's error string. Never anything
 * the buyer typed: no event here may carry an email address.
 *
 * `price` is optional for one caller only: the resume after signup or login
 * (lib/checkoutIntent.ts), which knows the product slug because it travelled in
 * the URL but does not know the price, because the course was never loaded on
 * that page. Reading it back off the query string would be putting a number a
 * visitor can edit into the funnel, so the field is left absent instead. Every
 * caller that has the price still sends it.
 *
 * Safe to fire on the line before `window.location.href = stripeUrl`: the SDK
 * POSTs /track with `keepalive: true`, so the request outlives the document
 * being discarded. It does NOT need to be awaited, and awaiting it would put a
 * network round trip in front of the redirect.
 */
export function trackCheckout(
  event: CheckoutEvent,
  props: { location: string; courseSlug: string; price?: number; reason?: string },
) {
  analytics.capture(event, props)
}

/**
 * Report a signup the waitlist API accepted, tagged with the same `source` the
 * WaitlistEntry row gets so the two systems can be reconciled.
 *
 * `surface` is the tag up to the first colon: `article:big-o` and
 * `article:react-keys` both roll up to `article`, which keeps a breakdown by
 * placement readable when every article tags its own slug.
 *
 * Takes the source only. The address is deliberately not a parameter, so this
 * cannot grow one by accident.
 */
export function trackNewsletterSignup(source: string) {
  analytics.capture(EVENT.newsletterSignup, {
    source,
    surface: source.split(':')[0],
  })
}

/**
 * Attributes to spread onto a CTA element so OpenPanel auto-tracks the click.
 *   <Link to="/mentorship" {...trackAttrs(CTA.mentorship, 'home_hero')}>
 *
 * `extras` become extra event properties (OpenPanel camelCases each `data-*`),
 * e.g. `trackAttrs(CTA.maven, 'article_cta', { target: 'senior-to-staff' })`
 * fires `cta_maven` with a `target` property alongside `location`.
 */
export function trackAttrs(
  event: CtaEvent,
  location: string,
  extras?: Record<string, string>,
) {
  const attrs: Record<string, string> = {
    'data-track': event,
    'data-location': location,
  }
  if (extras) {
    for (const [key, value] of Object.entries(extras)) {
      attrs[`data-${key}`] = value
    }
  }
  return attrs
}
